import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';
import ConfirmStateChangeDialog from './ConfirmStateChangeDialog';
import WaitingReasonDialog from './WaitingReasonDialog';
import CompletionDialog from './CompletionDialog';
import {
  requiresConfirmation,
  needsOdometerPrompt,
  getInvoiceReopenInfo,
  reopenSetStateOptions,
  setStateErrorMessage,
} from './stateTransitions';

/**
 * Shared work-order state-change controller. Used by the calendar card context menu today;
 * the work order page still runs its own (behaviour-identical) copy of this flow and can
 * migrate later.
 *
 * Mirrors MaintenanceProcessView.handleStateChange: completion dialog for
 * in_progress -> completed, waiting-reason dialog for -> waiting, confirm dialog when
 * requiresConfirmation or needsOdometerPrompt, direct PATCH otherwise. The maintenance is fetched fresh before
 * deciding, so the dialogs act on the real state and invoice flags rather than a stale
 * calendar card, and CompletionDialog gets the full object it needs.
 *
 * Returns handlers plus a `dialogs` node that the surface must keep mounted while a dialog
 * is open (same shape as worktime/clock/useClockController).
 *
 *   const { changeState, busy, dialogs } = useMaintenanceStateChange({ onChanged });
 *   changeState({ maintenanceId, targetState, mode: 'notify' | 'silent' });
 *   onChanged(detail, { targetState, via: 'set_state' | 'complete_with_notification' | 'none' })
 */
export function useMaintenanceStateChange({ onChanged } = {}) {
  const { t } = useTranslation();
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const canChangeState = can(currentOrganization, 'work_orders.change_state');
  const odometerRequired = !!currentOrganization?.require_odometer_on_completion;

  // pending: { kind: 'confirm' | 'waiting' | 'complete', maintenance, targetState, invoiceInfo? }
  const [pending, setPendingState] = useState(null);
  const pendingRef = useRef(null);
  const setPending = (p) => {
    pendingRef.current = p;
    setPendingState(p);
  };

  const [inFlight, setInFlightState] = useState(false);
  const inFlightRef = useRef(false);
  const setInFlight = (v) => {
    inFlightRef.current = v;
    setInFlightState(v);
  };

  const [snack, setSnack] = useState(null);
  const notify = (severity, message) => setSnack({ severity, message });

  const busy = inFlight || pending !== null;

  const emitChanged = (data, meta) => {
    if (onChanged) onChanged(data, meta);
  };

  const patchState = async (maintenance, targetState, options = {}) => {
    setInFlight(true);
    try {
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/set_state/`, {
        state: targetState,
        ...options,
      });
      emitChanged(response.data, { targetState, via: 'set_state' });
      notify('success', t('stateChange.changed', { state: t(`status.${targetState}`) }));
      return true;
    } catch (e) {
      const message = setStateErrorMessage(e, t);
      if (message) notify('error', message);
      return false;
    } finally {
      setInFlight(false);
    }
  };

  const changeState = async ({ maintenanceId, targetState, mode = 'notify' }) => {
    if (!canChangeState) return false;
    // Refuse a second request while one is in flight or a dialog is open (refs so that two
    // calls in the same tick cannot both pass).
    if (inFlightRef.current || pendingRef.current) return false;

    let fresh;
    setInFlight(true);
    try {
      const response = await axios.get(`/api/maintenances/${maintenanceId}/`);
      fresh = response.data;
    } catch (e) {
      notify('error', t('stateChange.loadFailed'));
      return false;
    } finally {
      setInFlight(false);
    }

    const current = fresh.state;
    if (current === targetState) {
      // The card was stale; repaint it from the fresh object, no duplicate state row.
      notify('info', t('stateChange.alreadyInState', { state: t(`status.${targetState}`) }));
      emitChanged(fresh, { targetState, via: 'none', alreadyInState: true });
      return false;
    }
    if (current === 'in_progress' && targetState === 'completed' && mode === 'notify') {
      setPending({ kind: 'complete', maintenance: fresh, targetState });
      return true;
    }
    if (targetState === 'waiting') {
      setPending({ kind: 'waiting', maintenance: fresh, targetState });
      return true;
    }
    // A finishing move on a work order without its own odometer reading always gets the
    // confirm dialog, which carries the reading field (even where the move needs no confirmation).
    if (requiresConfirmation(current, targetState) || needsOdometerPrompt(fresh, targetState)) {
      setPending({
        kind: 'confirm',
        maintenance: fresh,
        targetState,
        invoiceInfo: getInvoiceReopenInfo(fresh),
      });
      return true;
    }
    return patchState(fresh, targetState);
  };

  // Confirm from ConfirmStateChangeDialog / WaitingReasonDialog. The synchronous prefix
  // (read pending, set inFlight, clear pending) runs before ConfirmStateChangeDialog's own
  // handleClose call, so `busy` never drops while the PATCH is in flight.
  const confirmPending = async (extraOptions = {}) => {
    const p = pendingRef.current;
    if (!p) return;
    setInFlight(true);
    setPending(null);
    const options = {
      ...reopenSetStateOptions(p.maintenance.state, p.targetState, p.invoiceInfo),
      ...extraOptions,
    };
    await patchState(p.maintenance, p.targetState, options);
  };

  const dismissPending = () => setPending(null);

  const dialogs = (
    <>
      <ConfirmStateChangeDialog
        open={pending?.kind === 'confirm'}
        handleClose={dismissPending}
        onConfirm={(extra) => confirmPending(extra)}
        currentState={pending?.maintenance?.state}
        targetState={pending?.targetState}
        hasActiveBooking={pending?.maintenance?.has_active_booking}
        invoiceReopenInfo={pending?.invoiceInfo}
        maintenance={pending?.kind === 'confirm' ? pending.maintenance : null}
        odometerRequired={odometerRequired}
      />
      <WaitingReasonDialog
        open={pending?.kind === 'waiting'}
        handleClose={dismissPending}
        onConfirm={(reason) => confirmPending({ waiting_reason: reason })}
      />
      <CompletionDialog
        open={pending?.kind === 'complete'}
        handleClose={dismissPending}
        maintenance={pending?.maintenance ?? null}
        setMaintenance={(data) =>
          emitChanged(data, { targetState: 'completed', via: 'complete_with_notification' })
        }
      />
      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="state-change-snackbar"
      >
        <Alert
          severity={snack?.severity || 'info'}
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: '100%' }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </>
  );

  return { changeState, busy, dialogs };
}

export default useMaintenanceStateChange;
